import Cropper from "react-easy-crop";
import { Modal, confirm } from "@tw/tw-common-js";
import getCroppedImg from "../utils/cropImage";

import { useEffect, useState, useRef, useCallback, useContext } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import moment from "moment-timezone";
import api from "../services/api";
import { useAuth } from "../context/useAuth";
import { useDictionary } from "../context/useDictionary";
import { TIMEZONES_UTC } from "../services/timezones";
import MyProfilePersonalInfo from "../components/other/MyProfilePersonalInfo";
import MyProfileBankDetails from "../components/other/MyProfileBankDetails";
import MyProfilePerformance from "../components/other/MyProfilePerformance";

export default function MyProfile() {
  const { user } = useAuth();
  const { getDictionaryValue, getDictionaryTypeValues } = useDictionary();

  // State for cropping modal and image
  const [showCropModal, setShowCropModal] = useState(false);
  const [rawImgUrl, setRawImgUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [employee, setEmployee] = useState(null);
  const [originalEmployee, setOriginalEmployee] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [selectedTab, setSelectedTab] = useState("personalInfo");
  const [profileImgUrl, setProfileImgUrl] = useState(null);
  const [originalProfileImgUrl, setOriginalProfileImgUrl] = useState(null);
  const [pendingImgFile, setPendingImgFile] = useState(null);
  const [pendingImgUrl, setPendingImgUrl] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [imgError, setImgError] = useState(null);
  const fileInputRef = useRef(null);

  // Live clock — updates every minute
  const [currentTime, setCurrentTime] = useState(Date.now());
  const timerRef = useRef();
  useEffect(() => {
    timerRef.current = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(timerRef.current);
  }, []);

  const isDirty =
    editMode &&
    employee !== null &&
    originalEmployee !== null &&
    (JSON.stringify(employee) !== JSON.stringify(originalEmployee) || pendingImgFile !== null);

  // Block in-app navigation when dirty
  const confirmBlocker = useCallback(
    () =>
      confirm({
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to leave?",
      }),
    []
  );
  useNavigationBlocker(confirmBlocker, isDirty);

  // Block browser tab close when dirty
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const loadProfileImg = useCallback(async (hrCode) => {
    const res = await api.getProfileImgThumb(hrCode);
    if (res.status === 200 && res.data) {
      setProfileImgUrl(URL.createObjectURL(res.data));
    }
  }, []);

  useEffect(() => {
    async function fetchEmployee() {
      if (user?._employee) {
        const employee = await api.getPersonalProfile();
        if (employee.status === 200) {
          setEmployee(employee.data);
        } else {
          setEmployee({});
        }
      }
    }
    fetchEmployee();
  }, [user?._employee]);

  useEffect(() => {
    if (employee?.hrCode) {
      loadProfileImg(employee.hrCode);
    }
  }, [employee?.hrCode, loadProfileImg]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgError(null);
    // 15MB limit
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      setImgError("File too large. Max size is 15MB.");
      return;
    }
    if (pendingImgUrl) URL.revokeObjectURL(pendingImgUrl);
    setRawImgUrl(URL.createObjectURL(file));
    setShowCropModal(true);
    e.target.value = "";
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedBlob = await getCroppedImg(rawImgUrl, croppedAreaPixels);
      if (pendingImgUrl) URL.revokeObjectURL(pendingImgUrl);
      setPendingImgFile(new File([croppedBlob], "profile.jpg", { type: croppedBlob.type }));
      setPendingImgUrl(URL.createObjectURL(croppedBlob));
      setShowCropModal(false);
      setRawImgUrl(null);
    } catch (err) {
      console.error("Crop error:", err);
      setImgError("Failed to crop image.");
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setRawImgUrl(null);
  };

  const setValue = (key, value) => {
    setEmployee((prev) => ({ ...prev, [key]: value }));
  };

  const enterEditMode = () => {
    setOriginalEmployee({ ...employee });
    setOriginalProfileImgUrl(profileImgUrl);
    if (selectedTab === "performance") {
      setSelectedTab("personalInfo");
    }
    setEditMode(true);
    setSaveError(null);
  };

  const cancel = () => {
    setEmployee({ ...originalEmployee });
    setOriginalEmployee(null);
    if (pendingImgUrl) URL.revokeObjectURL(pendingImgUrl);
    setPendingImgFile(null);
    setPendingImgUrl(null);
    setProfileImgUrl(originalProfileImgUrl);
    setOriginalProfileImgUrl(null);
    setEditMode(false);
    setSaveError(null);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);

    // Upload pending image first
    if (pendingImgFile) {
      setUploadingImg(true);
      const imgRes = await api.uploadProfileImg(pendingImgFile);
      setUploadingImg(false);
      if (imgRes.status !== 200) {
        setSaveError(imgRes.data?.error || "Image upload failed.");
        setSaving(false);
        return;
      }
      await loadProfileImg(employee.hrCode);
      URL.revokeObjectURL(pendingImgUrl);
      setPendingImgFile(null);
      setPendingImgUrl(null);
    }

    const res = await api.saveEmployeeProfile(employee.id, employee.hrCode, employee);
    if (res.status === 200) {
      setEmployee(res.data);
      setOriginalEmployee(null);
      setOriginalProfileImgUrl(null);
      setEditMode(false);
    } else {
      setSaveError(res.data?.message || "An error occurred while saving.");
    }
    setSaving(false);
  };

  // Build timezone display: "UTC-4 (EDT) - 12:20 PM"
  const timezoneDisplay = (() => {
    if (!employee?.timezone) return null;
    const tzObj = TIMEZONES_UTC.find((tz) => tz.timezone === employee.timezone);
    if (!tzObj) return null;
    const tzTime = moment(currentTime).tz(tzObj.timezone).format("hh:mm A");
    const abbr = moment.tz(tzObj.timezone).zoneAbbr();
    return `${tzObj.label} (${abbr}) - ${tzTime}`;
  })();

  // Build subtitle: hrCode | Division | Department | Team | Country
  const subtitle = [
    employee?.hrCode,
    employee?.Team?.Department?.Division?.name,
    employee?.Team?.Department?.name,
    employee?.Team?.name,
    employee?.country,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <div className="my-profile-page">
      {showCropModal && (
        <Modal close={handleCropCancel} size="medium" noBodyPadding={true}>
          <div className="crop-modal-container">
            <div className="cropper-area">
              <Cropper
                image={rawImgUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
                restrictPosition={false}
                style={{ containerStyle: { cursor: "move" } }}
              />
            </div>
            <div className="crop-zoom-controls">
              <button
                className="crop-zoom-btn"
                onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
                aria-label="Zoom out"
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <rect x="4" y="9" width="12" height="2" rx="1" fill="currentColor" />
                </svg>
              </button>
              <span className="crop-zoom-label">{Math.round(zoom * 100)}%</span>
              <button
                className="crop-zoom-btn"
                onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
                aria-label="Zoom in"
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <rect x="4" y="9" width="12" height="2" rx="1" fill="currentColor" />
                  <rect x="9" y="4" width="2" height="12" rx="1" fill="currentColor" />
                </svg>
              </button>
            </div>
            <div className="crop-modal-actions">
              <button onClick={handleCropCancel}>Cancel</button>
              <button className="blue ml-10" onClick={handleCropSave}>
                Save
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* Profile Header — avatar + info + Edit button + tabs all in one card */}
      <div className="my-profile-header">
        <div className="my-profile-header-top">
          <div className="my-profile-avatar-wrapper">
            <div
              className={"my-profile-avatar" + (editMode ? " editable" : "")}
              title={editMode ? "Click to change profile picture" : undefined}
              onClick={() => editMode && fileInputRef.current?.click()}
            >
              {pendingImgUrl || profileImgUrl ? (
                <img src={pendingImgUrl || profileImgUrl} alt="Profile" />
              ) : (
                <span>
                  {employee?.firstName?.charAt(0)}
                  {employee?.lastName?.charAt(0)}
                </span>
              )}
              {editMode && (
                <div className="profile-img-overlay">
                  {uploadingImg ? (
                    <i className="material-icons rotating">autorenew</i>
                  ) : (
                    <i className="material-icons">photo_camera</i>
                  )}
                </div>
              )}
            </div>
            {imgError && <span className="error-message">{imgError}</span>}
            <input type="file" ref={fileInputRef} style={{ display: "none" }} accept="image/*" onChange={handleFileChange} />
          </div>

          <div className="my-profile-info">
            <div className="my-profile-name">
              {employee?.firstName} {employee?.lastName}
            </div>
            {subtitle && <div className="my-profile-subtitle">{subtitle}</div>}
            {employee?.email && <div className="my-profile-meta">{employee.email}</div>}
            {timezoneDisplay && <div className="my-profile-meta">{timezoneDisplay}</div>}
          </div>

          <div className="my-profile-header-actions">
            {editMode ? (
              <>
                <button onClick={cancel}>Cancel</button>
                <button className="blue ml-10" onClick={save} disabled={saving || !isDirty}>
                  <i className="material-symbols-outlined">save</i>
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            ) : (
              <button className="blue" onClick={enterEditMode}>
                <i className="material-symbols-outlined">edit</i>
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {saveError && <div className="error-message mx-24 mb-10">{saveError}</div>}

        {/* Tab bar at the bottom of the header */}
        <div className="my-profile-tab-bar">
          <div
            className={"my-profile-tab" + (selectedTab === "personalInfo" ? " active" : "")}
            onClick={() => setSelectedTab("personalInfo")}
          >
            Personal Information
          </div>
          <div
            className={"my-profile-tab" + (selectedTab === "bankDetails" ? " active" : "")}
            onClick={() => setSelectedTab("bankDetails")}
          >
            Bank Details
          </div>
          {!editMode && (
            <div
              className={"my-profile-tab" + (selectedTab === "performance" ? " active" : "")}
              onClick={() => setSelectedTab("performance")}
            >
              Performance Review
            </div>
          )}
        </div>
      </div>

      {/* Tab content — separate white boxes, scrollable */}
      <div className="my-profile-content">
        {selectedTab === "personalInfo" && (
          <MyProfilePersonalInfo
            employee={employee}
            getDictionaryValue={getDictionaryValue}
            getDictionaryTypeValues={getDictionaryTypeValues}
            editMode={editMode}
            setValue={setValue}
          />
        )}
        {selectedTab === "bankDetails" && <MyProfileBankDetails employee={employee} editMode={editMode} setValue={setValue} />}
        {selectedTab === "performance" && <MyProfilePerformance />}
      </div>
    </div>
  );
}

function useNavigationBlocker(blocker, when = true) {
  const { navigator } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (!when) return;

    const origPush = navigator.push;
    const origReplace = navigator.replace;

    navigator.push = async (...args) => {
      const confirmed = await blocker();
      if (confirmed) {
        navigator.push = origPush;
        navigator.replace = origReplace;
        origPush(...args);
      }
    };

    navigator.replace = async (...args) => {
      const confirmed = await blocker();
      if (confirmed) {
        navigator.push = origPush;
        navigator.replace = origReplace;
        origReplace(...args);
      }
    };

    return () => {
      navigator.push = origPush;
      navigator.replace = origReplace;
    };
  }, [blocker, when, navigator]);
}
